import React, { createContext, useCallback, useState } from 'react';
import { LoadingKeys } from "../models/enums/loading-keys.enum";

type LoadingKeyType = LoadingKeys | string;
type LoadingInput = LoadingKeyType | LoadingKeyType[];

interface LoadingContextType {
    isLoading: (scope: LoadingInput) => boolean;
    isAnyLoading: () => boolean;
    startLoading: (key: LoadingInput) => void;
    stopLoading: (key: LoadingInput) => void;
}

export const LoadingContext = createContext<LoadingContextType | null>(null);

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());

    const normalizeKeys = (key: LoadingInput): string[] => {
        if (Array.isArray(key)) {
            return key.map(k => String(k));
        }
        return [String(key)];
    };

    const startLoading = useCallback((key: LoadingInput) => {
        const keys = normalizeKeys(key);
        setLoadingKeys(prev => {
            const next = new Set(prev);
            keys.forEach(k => next.add(k));
            return next;
        });
    }, []);

    const stopLoading = useCallback((key: LoadingInput) => {
        const keys = normalizeKeys(key);
        setLoadingKeys(prev => {
            const next = new Set(prev);
            keys.forEach(k => next.delete(k));
            return next;
        });
    }, []);

    const isLoading = useCallback(
        (scope: LoadingInput): boolean => {
            const keys = normalizeKeys(scope);
            return keys.some(k => loadingKeys.has(k));
        },
        [loadingKeys]
    );

    const isAnyLoading = useCallback((): boolean => {
        return loadingKeys.size > 0;
    }, [loadingKeys]);

    return (
        <LoadingContext.Provider value={{ isLoading, isAnyLoading, startLoading, stopLoading }}>
            {children}
        </LoadingContext.Provider>
    );
};
